import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { openRazorpay } from "@/lib/razorpay";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FabricPicker from "@/components/bespoke/FabricPicker";
import ConfigOptionGroup from "@/components/bespoke/ConfigOptionGroup";
import AddressBook from "@/components/AddressBook";

const STEPS = ["Fabric", "Style", "Measure", "Pay"];
const INCH_OPTIONS = Array.from({ length: 20 }, (_, i) => i + 1);   // 1..20 in
const HEIGHT_FEET = Array.from({ length: 12 }, (_, i) => i + 1);    // 1..12 ft
const HEIGHT_INCH = Array.from({ length: 21 }, (_, i) => i);        // 0..20 in
const ytId = (url) => (String(url || "").match(/(?:v=|\.be\/|embed\/)([A-Za-z0-9_-]{11})/) || [])[1] || "";

function StepBar({ step, go, maxReached, prefix }) {
  return (
    <div className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-black/10">
      <div className="sq-container flex items-center gap-2 py-3 overflow-x-auto">
        {STEPS.map((s, i) => {
          const active = i === step, done = i < step, reachable = i <= maxReached;
          return (
            <button key={s} data-testid={`${prefix}-step-${i}`} disabled={!reachable} onClick={() => reachable && go(i)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm whitespace-nowrap border ${active ? "border-black bg-black text-white" : done ? "border-black/40" : "border-black/15 text-[#4B5563]"} ${!reachable ? "opacity-50 cursor-not-allowed" : ""}`}>
              <span className={`w-5 h-5 rounded-full grid place-items-center text-[11px] ${active ? "bg-white text-black" : done ? "bg-black text-white" : "bg-black/10"}`}>{done ? <Check size={12} /> : i + 1}</span>
              {s}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs text-[#4B5563]">{label}</label>
      <input {...props} className="w-full mt-1 bg-white border border-black/20 px-3 py-2 text-sm outline-none focus:border-squire-jeans" />
    </div>
  );
}

// config: { garmentType, title, prefix, groups[], measureFields[], guideCaption, faqs[] }
export default function BespokeWizard({ config }) {
  const { garmentType, title, prefix, groups, measureFields, guideCaption, faqs = [] } = config;
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const redirectPath = location.pathname + location.search;

  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [fabrics, setFabrics] = useState([]);
  const [fabric, setFabric] = useState(null);
  const [guide, setGuide] = useState({ youtube_url: "", images: [], intro: "" });

  const [selections, setSelections] = useState(() => Object.fromEntries(groups.map((g) => [g.group, g.default])));
  const [meas, setMeas] = useState(() => Object.fromEntries(measureFields.filter((f) => f.key !== "body_height").map((f) => [f.key, ""])));
  const hasStandard = !!(config.sizeChart && config.sizes);
  const [preference, setPreference] = useState(hasStandard ? "" : "custom");   // "" | custom | standard
  const [stdSize, setStdSize] = useState("");
  const [editSize, setEditSize] = useState(false);
  const [sizeOverrides, setSizeOverrides] = useState([]); // per-column values for the selected size
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [measuredFrom, setMeasuredFrom] = useState("garment");
  const [accepted, setAccepted] = useState(false);
  const [notes, setNotes] = useState("");
  const [address, setAddress] = useState({ label: "Home", line1: "", line2: "", city: "", state: "", pincode: "", phone: "" });
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [paying, setPaying] = useState(false);
  const [balanceMethod, setBalanceMethod] = useState("online"); // online | cod
  const categoryLabel = title.replace("Bespoke ", "");
  const bodyHeight = heightFt ? `${heightFt} ft${heightIn ? ` ${heightIn} in` : ""}` : "";

  useEffect(() => {
    api.get("/fabrics", { params: { garment: garmentType } }).then(({ data }) => setFabrics(data || [])).catch(() => {});
    api.get("/bespoke-guide", { params: { garment: garmentType } }).then(({ data }) => setGuide(data || {})).catch(() => {});
  }, [garmentType]);

  // Admin-configured measurement fields for this garment.
  const [adminMeasure, setAdminMeasure] = useState(null);
  useEffect(() => {
    api.get(`/bespoke-measure-fields/${garmentType}`).then(({ data }) => setAdminMeasure(data?.fields || [])).catch(() => setAdminMeasure([]));
  }, [garmentType]);

  // Body height is always its own feet+inch field; admin controls the rest.
  const effectiveMeasureFields = (adminMeasure && adminMeasure.length)
    ? [{ key: "body_height", label: "Body Height *", target: "style", required: true },
       ...adminMeasure.map((f) => ({ key: f.key, label: `${f.label}${f.required ? " *" : ""}`, unit: f.unit || "in", target: "meas", required: !!f.required }))]
    : measureFields;

  // Keep the meas state in sync when the field set changes.
  useEffect(() => {
    setMeas((prev) => {
      const next = {};
      effectiveMeasureFields.filter((f) => f.key !== "body_height").forEach((f) => { next[f.key] = prev[f.key] || ""; });
      return next;
    });
  }, [adminMeasure]); // eslint-disable-line react-hooks/exhaustive-deps

  // Admin-configured option groups for this garment (types, styles, advanced sections).
  const [adminGroups, setAdminGroups] = useState(null);
  useEffect(() => {
    api.get(`/bespoke-option-groups/${garmentType}`).then(({ data }) => {
      const og = data?.option_groups || [];
      if (og.length) {
        setAdminGroups(og.map((g) => ({
          group: g.key,
          label: (g.label || g.key) + (g.required ? " *" : ""),
          advanced: !!g.advanced,
          cols: g.cols || "",
          options: (g.options || []).map((o) => ({ key: o.key, label: o.label, image: o.image || "", price: Number(o.price) || 0 })),
          default: (g.options || [])[0]?.key,
          showPrice: (g.options || []).some((o) => Number(o.price) > 0),
        })));
      } else setAdminGroups([]);
    }).catch(() => setAdminGroups([]));
  }, [garmentType]);

  // Sections are fully admin-driven (seeded defaults live in the DB and are
  // editable per garment). No hardcoded fallback.
  const effectiveGroups = adminGroups || [];
  const inlineGroups = effectiveGroups.filter((g) => !g.advanced);
  const advancedGroups = effectiveGroups.filter((g) => g.advanced);

  // Make sure every group has a current selection (defaults) once admin groups load.
  useEffect(() => {
    setSelections((prev) => {
      const next = { ...prev };
      effectiveGroups.forEach((g) => { if (next[g.group] === undefined) next[g.group] = g.default ?? g.options?.[0]?.key; });
      return next;
    });
  }, [adminGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const style = useMemo(() => {
    return { ...selections, preference, measured_from: measuredFrom, body_height: bodyHeight, standard_size: preference === "standard" ? stdSize : "", size_adjusted: preference === "standard" && (editSize || sizeOverrides.length > 0), notes };
  }, [selections, preference, measuredFrom, bodyHeight, stdSize, editSize, sizeOverrides, notes]);

  const measurement = useMemo(() => {
    const m = { profile_name: "Self" };
    if (preference === "standard") {
      m.size = stdSize;
      // Attach the (possibly customer-adjusted) size-chart values so they show
      // in the order + user dashboard.
      if (stdSize && config.sizeChart) {
        const row = config.sizeChart.rows.find((r) => r.size === stdSize);
        config.sizeChart.columns.forEach((c, i) => {
          const key = c.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
          const raw = (sizeOverrides.length && sizeOverrides[i] !== "" && sizeOverrides[i] != null) ? sizeOverrides[i] : row?.values[i];
          const num = parseFloat(raw);
          m[key] = isNaN(num) ? raw : num;
        });
        m.size_adjusted = editSize || (sizeOverrides.length > 0);
      }
      return m;
    }
    effectiveMeasureFields.filter((f) => f.target === "meas").forEach((f) => { const v = parseFloat(meas[f.key]); m[f.key] = isNaN(v) ? null : v; });
    return m;
  }, [meas, effectiveMeasureFields, preference, stdSize, sizeOverrides, editSize, config.sizeChart]);

  // Authoritative price from the server whenever fabric/selections change.
  useEffect(() => {
    if (!fabric) { setBreakdown(null); return; }
    let cancelled = false;
    api.post("/payments/bespoke/quote", { fabric_id: fabric.id, garment_type: garmentType, measurement, style, address })
      .then(({ data }) => { if (!cancelled) setBreakdown(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [fabric, JSON.stringify(selections), garmentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = breakdown?.total || 0;
  const gstPct = Math.round((breakdown?.gst_rate ?? 0.18) * 100);
  const shipping = breakdown?.shipping || 0;
  const advance = breakdown?.advance || 0;
  const balance = breakdown?.balance || 0;
  const advancePct = Math.round((breakdown?.advance_pct ?? 0.4) * 100);
  const balancePct = 100 - advancePct;
  const go = (n) => { setStep(n); setMaxReached((m) => Math.max(m, n)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const next = () => go(Math.min(step + 1, STEPS.length - 1));
  const back = () => go(Math.max(step - 1, 0));

  const requiredMeas = effectiveMeasureFields.filter((f) => f.required && f.key !== "body_height").map((f) => f.key);
  const customOk = heightFt && requiredMeas.every((k) => String(meas[k] || "").trim());
  const editedComplete = !editSize || (sizeOverrides.length > 0 && sizeOverrides.every((v) => String(v).trim() !== ""));
  const canLeaveMeasure = accepted && (preference === "standard" ? (!!stdSize && editedComplete) : customOk);
  const addressOk = address.line1 && address.city && address.state && address.pincode && address.phone;

  const pay = async () => {
    if (!user) { toast("Please sign in to place your order"); nav(`/auth?redirect=${encodeURIComponent(redirectPath)}`); return; }
    if (!fabric) { toast.error("Select a fabric first"); go(0); return; }
    if (!accepted) { toast.error("Please accept the measurement confirmation"); go(2); return; }
    if (!addressOk) { toast.error("Please complete your delivery address"); return; }
    setPaying(true);
    try {
      const payload = { fabric_id: fabric.id, garment_type: garmentType, measurement, style, address, balance_method: balanceMethod };
      const { data: intent } = await api.post("/payments/bespoke/create", payload);
      await openRazorpay(intent, {
        name: "Squire & Squire",
        description: `${title} — ${fabric.name}`,
        onSuccess: async (resp) => {
          try {
            const { data: order } = await api.post("/payments/bespoke/verify", { intent_id: intent.intent_id, ...resp });
            toast.success("Payment successful — order confirmed!");
            nav(`/order-confirmed/${order.id}`, { replace: true });
          } catch (e) { toast.error("Payment could not be verified. If money was deducted, contact us."); }
          finally { setPaying(false); }
        },
        onDismiss: () => { setPaying(false); toast("Payment cancelled"); },
      });
    } catch (e) { setPaying(false); toast.error(e?.response?.data?.detail || "Could not start payment"); }
  };

  const set = (group, key) => setSelections((s) => ({ ...s, [group]: key }));

  // Renders one option group. Uses image cards when the admin supplied images,
  // otherwise falls back to the icon-based ConfigOptionGroup.
  const renderGroup = (g) => {
    const hasImages = g.options?.some((o) => o.image);
    return (
      <div key={g.group} className="bg-white border border-black/10 rounded-2xl p-5 md:p-6">
        <div className="text-sm font-semibold uppercase tracking-[0.08em] text-black/80 mb-4">{g.label}</div>
        {hasImages ? (
          <div className={`grid ${g.cols || "grid-cols-2 sm:grid-cols-3 md:grid-cols-4"} gap-3`}>
            {g.options.map((o) => {
              const on = selections[g.group] === o.key;
              return (
                <button key={o.key} onClick={() => set(g.group, o.key)} data-testid={`${prefix}-${g.group}-${o.key}`}
                  className={`group relative rounded-xl border-2 text-center transition-all duration-200 overflow-hidden ${on ? "border-squire-jeans bg-squire-jeans/[0.04] shadow-sm" : "border-black/10 hover:border-black/30 hover:shadow-sm"}`}>
                  {on && <span className="absolute top-2 right-2 z-10 w-5 h-5 rounded-full bg-squire-jeans text-white grid place-items-center"><Check size={12} /></span>}
                  {o.image && <div className="aspect-square bg-[#F7F7F5] overflow-hidden"><img src={o.image} alt={o.label} className="w-full h-full object-cover" /></div>}
                  <div className="text-sm py-2.5 px-1 font-medium">{o.label}{o.price > 0 ? <span className="text-squire-jeans"> +₹{o.price}</span> : ""}</div>
                </button>
              );
            })}
          </div>
        ) : (
          <ConfigOptionGroup group={g.group} fallbackOptions={g.options} FallbackIcon={g.Icon} selected={selections[g.group]}
            onChange={(k) => set(g.group, k)} testidPrefix={`${prefix}-${g.group}`} cols={g.cols} showPrice={g.showPrice} />
        )}
      </div>
    );
  };

  return (
    <main data-testid={`bespoke-category-${garmentType}`} className="pb-24 lg:pb-0 bg-[#FAFAF8] min-h-screen">
      <div className="bg-white border-b border-black/5">
      <div className="sq-container pt-6 pb-4">
        <div className="sq-eyebrow text-[#4B5563] flex items-center gap-2">
          <Link to="/" className="hover:text-squire-jeans">Home</Link> /
          <Link to="/bespoke" className="hover:text-squire-jeans">Bespoke</Link> /
          <span className="text-black">{title.replace("Bespoke ", "")}</span>
        </div>
        <h1 className="font-display text-3xl md:text-5xl mt-2">{title}</h1>
      </div>
      </div>

      <div className="sq-container grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 pt-8">
        <div className="min-h-[50vh] space-y-14">
          {(
            <section data-testid={`${prefix}-wizard-fabric`}>
              <h2 className="font-display text-2xl mb-1">Choose your fabric</h2>
              <p className="text-sm text-[#4B5563] mb-6">Your price is set by the fabric you pick. Add more anytime from Admin → Fabrics.</p>
              <FabricPicker fabrics={fabrics} selected={fabric} onSelect={setFabric} testidPrefix={`${prefix}-fabric`} />
            </section>
          )}

          {(
            <section data-testid={`${prefix}-wizard-style`} className="space-y-5">
              <div>
                <h2 className="font-display text-2xl md:text-3xl">Styling & Customization</h2>
                <p className="text-sm text-[#4B5563] mt-1">Make it yours — pick a look for each detail below.</p>
              </div>
              {adminGroups === null && <div className="text-sm text-[#4B5563]">Loading options…</div>}
              {adminGroups !== null && effectiveGroups.length === 0 && (
                <div className="border border-dashed border-black/20 rounded-lg p-6 text-sm text-[#4B5563]">No customization options are configured for this garment yet.</div>
              )}
              <div className="grid grid-cols-1 gap-4">
                {inlineGroups.map(renderGroup)}
              </div>

              {advancedGroups.length > 0 && (
                <Accordion type="single" collapsible className="border border-black/10 rounded-lg px-4" data-testid={`${prefix}-advanced`}>
                  <AccordionItem value="advanced" className="border-none">
                    <AccordionTrigger className="text-left py-4">
                      <div>
                        <div className="font-display text-xl">Advanced Customization</div>
                        <div className="text-xs text-squire-jeans font-medium mt-0.5">Want a product that exactly fits your imagination? Click here →</div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 space-y-8">
                      {advancedGroups.map(renderGroup)}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
            </section>
          )}

          {(
            <section data-testid={`${prefix}-wizard-measure`} className="space-y-8">
              {hasStandard && (
                <div>
                  <h2 className="font-display text-2xl md:text-3xl mb-1">Select Your Preference *</h2>
                  <p className="text-sm text-[#4B5563]">Choose how you'd like your fit — the rest of the form appears once you pick.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 max-w-2xl">
                    <button data-testid={`${prefix}-pref-custom`} onClick={() => setPreference("custom")} className={`py-5 border-2 rounded-xl text-sm font-medium transition-all ${preference === "custom" ? "border-squire-jeans bg-squire-jeans/[0.04] shadow-sm" : "border-black/15 hover:border-black/40"}`}>Customized For You<div className="text-[11px] text-[#4B5563] font-normal mt-1">Enter your own measurements</div></button>
                    <button data-testid={`${prefix}-pref-standard`} onClick={() => setPreference("standard")} className={`py-5 border-2 rounded-xl text-sm font-medium transition-all ${preference === "standard" ? "border-squire-jeans bg-squire-jeans/[0.04] shadow-sm" : "border-black/15 hover:border-black/40"}`}>Standard Size<div className="text-[11px] text-[#4B5563] font-normal mt-1">Pick from our size chart</div></button>
                  </div>
                </div>
              )}

              {/* STANDARD SIZE */}
              {hasStandard && preference === "standard" && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between max-w-2xl mb-2">
                      <div className="text-sm font-medium">Select Size *</div>
                      {stdSize && stdSize !== "__custom" && (
                        <button type="button" data-testid={`${prefix}-size-edit`} onClick={() => {
                          if (!editSize) {
                            const row = config.sizeChart.rows.find((r) => r.size === stdSize);
                            setSizeOverrides(config.sizeChart.columns.map((_, i) => String(row?.values[i] ?? "")));
                          }
                          setEditSize((v) => !v);
                        }} className="text-xs uppercase tracking-[0.15em] border border-black/25 hover:border-black rounded px-3 py-1.5">
                          {editSize ? "Done" : "✎ Adjust my measurements"}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 max-w-2xl">
                      {config.sizes.map((s) => (
                        <button key={s} data-testid={`${prefix}-size-${s}`} onClick={() => { setStdSize(s); setEditSize(false); }} className={`px-6 py-3 border text-sm ${stdSize === s ? "border-black bg-black text-white" : "border-black/25 hover:border-black"}`}>{s}</button>
                      ))}
                    </div>
                    {editSize && stdSize && stdSize !== "__custom" && (
                      <p className="text-[11px] text-squire-jeans mt-2">Editing size {stdSize} just for you — tweak any value below and we'll tailor to it.</p>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse" data-testid={`${prefix}-size-chart`}>
                      <thead>
                        <tr className="bg-[#EDEDED]">
                          <th className="border border-black/15 py-3 px-4 font-medium">Size</th>
                          {config.sizeChart.columns.map((c) => <th key={c} className="border border-black/15 py-3 px-4 font-medium text-center">{c}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {config.sizeChart.rows.map((r) => {
                          const isSel = stdSize === r.size;
                          const editing = editSize && isSel;
                          return (
                            <tr key={r.size} className={isSel ? "bg-squire-jeans/5" : ""}>
                              <td className="border border-black/15 py-3 px-4 font-semibold text-center">{r.size}</td>
                              {r.values.map((v, i) => (
                                <td key={i} className="border border-black/15 py-2 px-2 text-center">
                                  {editing ? (
                                    <input data-testid={`${prefix}-size-input-${i}`} value={sizeOverrides[i] ?? ""} onChange={(e) => { const nv = [...sizeOverrides]; nv[i] = e.target.value; setSizeOverrides(nv); }} className="w-16 border border-squire-jeans/50 rounded px-1.5 py-1 text-center text-sm" />
                                  ) : (isSel && sizeOverrides.length && sizeOverrides[i] !== "" ? sizeOverrides[i] : v)}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* CUSTOMIZED FOR YOU */}
              {(!hasStandard || preference === "custom") && (
                <>
                  <div className="max-w-2xl">
                    <div className="text-sm font-medium mb-2">Measured from? *</div>
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <button data-testid={`${prefix}-measuredfrom-body`} onClick={() => setMeasuredFrom("body")} className={`py-3 border text-sm ${measuredFrom === "body" ? "border-black bg-[#F5F5F5]" : "border-black/20 hover:border-black"}`}>Body</button>
                      <button data-testid={`${prefix}-measuredfrom-garment`} onClick={() => setMeasuredFrom("garment")} className={`py-3 border text-sm ${measuredFrom === "garment" ? "border-black bg-[#F5F5F5]" : "border-black/20 hover:border-black"}`}>{categoryLabel}</button>
                    </div>

                    {/* Body height — feet + inch dropdowns */}
                    <div className="mb-5">
                      <label className="text-xs text-[#4B5563]">Body Height *</label>
                      <div className="grid grid-cols-2 gap-4 mt-1">
                        <select data-testid={`${prefix}-height-ft`} value={heightFt} onChange={(e) => setHeightFt(e.target.value)} className="bg-white border border-black/20 px-3 py-2 text-sm outline-none focus:border-squire-jeans">
                          <option value="">Feet…</option>
                          {HEIGHT_FEET.map((n) => <option key={n} value={n}>{n} ft</option>)}
                        </select>
                        <select data-testid={`${prefix}-height-in`} value={heightIn} onChange={(e) => setHeightIn(e.target.value)} className="bg-white border border-black/20 px-3 py-2 text-sm outline-none focus:border-squire-jeans">
                          <option value="">Inch…</option>
                          {HEIGHT_INCH.map((n) => <option key={n} value={n}>{n} in</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Other measurement fields — inch dropdowns */}
                    <div className="grid grid-cols-2 gap-4">
                      {effectiveMeasureFields.filter((f) => f.key !== "body_height").map((f) => {
                        const unit = f.unit || "in";
                        const range = unit === "cm" ? [20, 160] : unit === "ft" ? [1, 8] : [1, 60];
                        const opts = Array.from({ length: range[1] - range[0] + 1 }, (_, i) => i + range[0]);
                        return (
                          <div key={f.key}>
                            <label className="text-xs text-[#4B5563]">{f.label}</label>
                            <select data-testid={`${prefix}-meas-${f.key}`} value={meas[f.key] || ""} onChange={(e) => setMeas({ ...meas, [f.key]: e.target.value })} className="w-full mt-1 bg-white border border-black/20 px-3 py-2 text-sm outline-none focus:border-squire-jeans">
                              <option value="">Select…</option>
                              {opts.map((n) => <option key={n} value={n}>{n} {unit}</option>)}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              <div className="max-w-2xl">
                <div className="text-sm font-medium mb-2">Make your acceptance *</div>
                <label className="flex items-center gap-2 text-sm">
                  <input data-testid={`${prefix}-accept`} type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="accent-squire-jeans w-4 h-4" />
                  I agree that all the measurements are up to my Best Fit.
                </label>
              </div>

              <div className="max-w-2xl">
                <div className="text-lg font-display">Have any special requirements? (Optional)</div>
                <p className="text-xs text-[#4B5563] mb-1">Please note that additional charges may apply according to your requirements.</p>
                <textarea data-testid={`${prefix}-special-notes`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full mt-1 bg-white border border-black/20 px-3 py-2 text-sm outline-none focus:border-squire-jeans" placeholder="Monogram, colour-match, anything our tailors should know." />
              </div>
            </section>
          )}

          {(
            <section data-testid={`${prefix}-wizard-pay`} className="max-w-2xl space-y-6">
              <h2 className="font-display text-2xl">Review & payment</h2>

              {!user && (
                <div className="border-2 border-squire-jeans/40 bg-squire-jeans/[0.05] rounded-xl p-6 text-center" data-testid={`${prefix}-auth-gate`}>
                  <Lock size={26} className="mx-auto text-squire-jeans" />
                  <h3 className="font-display text-2xl mt-3">Sign up to proceed further</h3>
                  <p className="text-sm text-[#4B5563] mt-2 max-w-sm mx-auto">Please sign in or create an account to confirm your bespoke order and make the payment.</p>
                  <button data-testid={`${prefix}-auth-gate-btn`} onClick={() => nav(`/auth?redirect=${encodeURIComponent(redirectPath)}`)} className="sq-btn-primary mt-5">
                    Sign in / Sign up <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {user && (<>

              {/* Full order details */}
              <div className="border border-black/10 p-5 space-y-2 text-sm" data-testid={`${prefix}-order-summary`}>
                <div className="sq-eyebrow text-squire-jeans mb-1">Order Summary</div>
                <div className="flex justify-between"><span className="text-[#4B5563]">Garment</span><span className="font-medium">{categoryLabel}</span></div>
                <div className="flex justify-between"><span className="text-[#4B5563]">Fabric</span><span className="font-medium text-right">{fabric ? `${fabric.name}${fabric.colors?.length ? ` · ${fabric.colors.join(", ")}` : ""}` : "—"}</span></div>
                {hasStandard && preference === "standard"
                  ? <div className="flex justify-between"><span className="text-[#4B5563]">Size</span><span className="font-medium">{stdSize || "—"}</span></div>
                  : <>
                      <div className="flex justify-between"><span className="text-[#4B5563]">Fit</span><span className="font-medium">Custom measurements</span></div>
                      {bodyHeight && <div className="flex justify-between"><span className="text-[#4B5563]">Height</span><span className="font-medium">{bodyHeight}</span></div>}
                    </>}
                {effectiveGroups.filter((g) => selections[g.group]).slice(0, 8).map((g) => (
                  <div key={g.group} className="flex justify-between"><span className="text-[#4B5563] capitalize">{g.group.replace(/_/g, " ")}</span><span className="font-medium capitalize">{(g.options.find((o) => o.key === selections[g.group])?.label) || selections[g.group]}</span></div>
                ))}
                {notes && <div className="flex justify-between"><span className="text-[#4B5563]">Special note</span><span className="font-medium text-right max-w-[60%]">{notes}</span></div>}
              </div>

              <div>
                <div className="sq-eyebrow text-squire-jeans mb-2">Delivery Address</div>
                <AddressBook selectedId={selectedAddrId} onSelect={(a) => { setSelectedAddrId(a.id); setAddress({ label: a.label, line1: a.line1, line2: a.line2, city: a.city, state: a.state, pincode: a.pincode, phone: a.phone }); }} />
              </div>

              <div className="border border-black/10 p-5">
                <div className="flex justify-between text-sm"><span className="text-[#4B5563]">Fabric {fabric ? `(${fabric.name})` : ""}</span><span data-testid={`${prefix}-pay-fabric`}>{formatINR(breakdown?.fabric_cost || 0)}</span></div>
                <div className="flex justify-between text-sm mt-2"><span className="text-[#4B5563]">GST ({gstPct}%)</span><span data-testid={`${prefix}-pay-gst`}>{formatINR(breakdown?.gst || 0)}</span></div>
                <div className="flex justify-between text-sm mt-2"><span className="text-[#4B5563]">Shipping</span><span data-testid={`${prefix}-pay-shipping`}>{shipping > 0 ? formatINR(shipping) : <span className="text-green-700 font-medium">FREE</span>}</span></div>
                <div className="flex justify-between font-display text-2xl mt-3 pt-3 border-t border-black/10"><span>Total</span><span data-testid={`${prefix}-pay-total`}>{formatINR(total)}</span></div>
                <div className="mt-4 pt-3 border-t border-black/10 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-black font-medium">Pay now ({advancePct}%)</span><span className="font-semibold" data-testid={`${prefix}-pay-advance`}>{formatINR(advance)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#4B5563]">Balance ({balancePct}%) — on delivery</span><span data-testid={`${prefix}-pay-balance`}>{formatINR(balance)}</span></div>
                </div>
              </div>

              {/* How the balance is paid on delivery */}
              <div>
                <div className="text-sm font-medium mb-2">Balance ({balancePct}%) on delivery — how would you like to pay it?</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button data-testid={`${prefix}-balance-online`} onClick={() => setBalanceMethod("online")} className={`p-4 text-left border text-sm ${balanceMethod === "online" ? "border-black bg-[#F5F5F5]" : "border-black/20 hover:border-black"}`}>
                    <div className="font-medium">Pay online on delivery</div>
                    <div className="text-xs text-[#4B5563] mt-1">A secure link to pay {formatINR(balance)} appears once delivered.</div>
                  </button>
                  <button data-testid={`${prefix}-balance-cod`} onClick={() => setBalanceMethod("cod")} className={`p-4 text-left border text-sm ${balanceMethod === "cod" ? "border-black bg-[#F5F5F5]" : "border-black/20 hover:border-black"}`}>
                    <div className="font-medium">Cash on Delivery</div>
                    <div className="text-xs text-[#4B5563] mt-1">Pay {formatINR(balance)} in cash to the courier at your door.</div>
                  </button>
                </div>
              </div>

              <button data-testid={`${prefix}-pay-now`} disabled={paying || !fabric} onClick={pay} className="sq-btn-primary w-full justify-center">
                <Lock size={15} /> {paying ? "Opening payment…" : `Pay ${formatINR(advance)} now & Confirm Order`}
              </button>
              <p className="text-xs text-[#4B5563] flex items-center gap-2"><ShieldCheck size={14} className="text-squire-jeans" /> Pay {advancePct}% now to confirm your order. The remaining {formatINR(balance)} is {balanceMethod === "cod" ? "collected in cash on delivery" : "payable online after delivery"}.</p>
              </>)}
            </section>
          )}
        </div>

        <aside className="hidden lg:block">
          <div className="sticky top-28 bg-white border border-black/10 rounded-2xl p-6 shadow-sm">
            <div className="sq-eyebrow text-squire-jeans">Your Build</div>
            <div className="mt-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#4B5563]">Fabric</span><span>{fabric ? formatINR(breakdown?.fabric_cost || 0) : "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#4B5563]">GST {gstPct}%</span><span>{formatINR(breakdown?.gst || 0)}</span></div>
              <div className="flex justify-between"><span className="text-[#4B5563]">Shipping</span><span>{shipping > 0 ? formatINR(shipping) : "FREE"}</span></div>
            </div>
            <div className="flex justify-between font-display text-xl mt-3 pt-3 border-t border-black/10"><span>Total</span><span data-testid={`${prefix}-rail-total`}>{formatINR(total)}</span></div>
            <div className="flex justify-between text-xs mt-2"><span className="text-black font-medium">Pay now ({advancePct}%)</span><span className="font-semibold">{formatINR(advance)}</span></div>
            <div className="flex justify-between text-xs mt-1"><span className="text-[#4B5563]">Balance on delivery</span><span>{formatINR(balance)}</span></div>
            {fabric && <div className="text-xs text-[#4B5563] mt-2">Fabric: {fabric.name}</div>}
          </div>
        </aside>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-black/10 p-3 flex items-center justify-between lg:hidden">
        <div>
          <div className="font-display text-lg">{formatINR(advance)}</div>
          <div className="text-[10px] text-[#4B5563]">pay now · {advancePct}%</div>
        </div>
        <button data-testid={`${prefix}-mobile-pay`} disabled={paying || !fabric} onClick={pay} className="sq-btn-primary">Pay Now</button>
      </div>
    </main>
  );
}
